import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useCurrency } from '../hooks/useCurrency';
import { searchHotels, Hotel } from '../services/hotels';

const Hotels: React.FC = () => {
    const { t } = useTranslation();
    const { formatPrice } = useCurrency();
    const [hotels, setHotels] = useState<Hotel[]>([]);
    const [filteredHotels, setFilteredHotels] = useState<Hotel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCity, setSelectedCity] = useState('');
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [selectedRating, setSelectedRating] = useState(0);
    const [sortBy, setSortBy] = useState('name');

    // Sample cities for filter
    const cities = ['Istanbul', 'Dubai', 'Paris', 'London', 'New York', 'Tokyo', 'Singapore', 'Bangkok'];

    useEffect(() => {
        // Load hotels
        const loadHotels = async () => {
            try {
                const hotelData = await searchHotels({});
                setHotels(hotelData);
                setFilteredHotels(hotelData);
            } catch (error) {
                console.error('Error loading hotels:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadHotels();
    }, []);

    useEffect(() => {
        // Apply filters
        let filtered = hotels;

        // Search term filter
        if (searchTerm) {
            filtered = filtered.filter(hotel =>
                hotel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (hotel.address && hotel.address.toLowerCase().includes(searchTerm.toLowerCase()))
            );
        }

        // City filter
        if (selectedCity) {
            filtered = filtered.filter(hotel =>
                hotel.address && hotel.address.toLowerCase().includes(selectedCity.toLowerCase())
            );
        }

        // Price filter
        filtered = filtered.filter(hotel =>
            hotel.priceFrom && hotel.priceFrom >= priceRange[0] && hotel.priceFrom <= priceRange[1]
        );

        // Rating filter
        if (selectedRating > 0) {
            filtered = filtered.filter(hotel => hotel.rating && hotel.rating >= selectedRating);
        }

        // Sort
        filtered.sort((a, b) => {
            switch (sortBy) {
                case 'price':
                    return (a.priceFrom || 0) - (b.priceFrom || 0);
                case 'rating':
                    return (b.rating || 0) - (a.rating || 0);
                case 'name':
                default:
                    return a.name.localeCompare(b.name);
            }
        });

        setFilteredHotels(filtered);
    }, [hotels, searchTerm, selectedCity, priceRange, selectedRating, sortBy]);

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCity('');
        setPriceRange([0, 1000]);
        setSelectedRating(0);
        setSortBy('name');
    };

    if (isLoading) {
        return (
            <div className="hotels-page">
                <div className="container">
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>{t('common.loading')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="hotels-page">
            <div className="container">
                {/* Hero Section */}
                <motion.section 
                    className="hotels-hero"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1>{t('hotels.title', 'Hotels')}</h1>
                    <p>{t('hotels.subtitle', 'Discover the perfect accommodation for your journey')}</p>
                </motion.section>

                {/* Filters Section */}
                <motion.section 
                    className="hotels-filters"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <div className="filters-header">
                        <h3>{t('hotels.filters', 'Filters')}</h3>
                        <button onClick={clearFilters} className="clear-filters-btn">
                            {t('hotels.clearFilters', 'Clear All')}
                        </button>
                    </div>

                    <div className="filters-grid">
                        {/* Search */}
                        <div className="filter-group">
                            <label>{t('hotels.search', 'Search Hotels')}</label>
                            <input
                                type="text"
                                placeholder={t('hotels.searchPlaceholder', 'Hotel name or location...')}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="filter-input"
                            />
                        </div>

                        {/* City */}
                        <div className="filter-group">
                            <label>{t('hotels.city', 'City')}</label>
                            <select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                className="filter-select"
                            >
                                <option value="">{t('hotels.allCities', 'All Cities')}</option>
                                {cities.map(city => (
                                    <option key={city} value={city}>{city}</option>
                                ))}
                            </select>
                        </div>

                        {/* Price Range */}
                        <div className="filter-group">
                            <label>{t('hotels.priceRange', 'Price Range')}</label>
                            <div className="price-range">
                                <input
                                    type="number"
                                    placeholder="Min"
                                    value={priceRange[0]}
                                    onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                                    className="price-input"
                                />
                                <span>-</span>
                                <input
                                    type="number"
                                    placeholder="Max"
                                    value={priceRange[1]}
                                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 1000])}
                                    className="price-input"
                                />
                            </div>
                        </div>

                        {/* Rating */}
                        <div className="filter-group">
                            <label>{t('hotels.minRating', 'Minimum Rating')}</label>
                            <div className="rating-filter">
                                {[1, 2, 3, 4, 5].map(rating => (
                                    <button
                                        key={rating}
                                        className={`rating-btn ${selectedRating === rating ? 'active' : ''}`}
                                        onClick={() => setSelectedRating(selectedRating === rating ? 0 : rating)}
                                    >
                                        {'⭐'.repeat(rating)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Sort */}
                        <div className="filter-group">
                            <label>{t('hotels.sortBy', 'Sort By')}</label>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="filter-select"
                            >
                                <option value="name">{t('hotels.sortName', 'Name')}</option>
                                <option value="price">{t('hotels.sortPrice', 'Price')}</option>
                                <option value="rating">{t('hotels.sortRating', 'Rating')}</option>
                            </select>
                        </div>
                    </div>
                </motion.section>

                {/* Results Section */}
                <motion.section 
                    className="hotels-results"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.4 }}
                >
                    <div className="results-header">
                        <h3>
                            {t('hotels.results', 'Results')} ({filteredHotels.length})
                        </h3>
                        <p>
                            {filteredHotels.length > 0 
                                ? t('hotels.hotelsFound', 'hotels found')
                                : t('hotels.noHotels', 'No hotels found matching your criteria')
                            }
                        </p>
                    </div>

                    {filteredHotels.length === 0 ? (
                        <div className="no-results">
                            <div className="no-results-icon">🏨</div>
                            <h4>{t('hotels.noResultsTitle', 'No Hotels Found')}</h4>
                            <p>{t('hotels.noResultsDesc', 'Try adjusting your filters or search criteria')}</p>
                            <button onClick={clearFilters} className="btn btn-primary">
                                {t('hotels.clearFilters', 'Clear Filters')}
                            </button>
                        </div>
                    ) : (
                        <div className="hotels-grid">
                            {filteredHotels.map((hotel, index) => (
                                <motion.div
                                    key={hotel.id}
                                    className="hotel-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: index * 0.1 }}
                                    whileHover={{ y: -5, scale: 1.02 }}
                                >
                                    <div className="hotel-image">
                                        <img src={hotel.imageUrl} alt={hotel.name} />
                                        <div className="hotel-rating">
                                            {'⭐'.repeat(hotel.rating || 0)}
                                        </div>
                                        <div className="hotel-price">
                                            {formatPrice(hotel.priceFrom || 0)}
                                        </div>
                                    </div>
                                    <div className="hotel-content">
                                        <h4 className="hotel-name">{hotel.name}</h4>
                                        <p className="hotel-address">{hotel.address}</p>
                                        <div className="hotel-features">
                                            <span className="feature">WiFi</span>
                                            <span className="feature">Pool</span>
                                            <span className="feature">Gym</span>
                                        </div>
                                        <div className="hotel-actions">
                                            <Link 
                                                to={`/hotel/${hotel.id}`} 
                                                className="btn btn-secondary hotel-view-btn"
                                            >
                                                {t('hotelDetails.viewDetails')}
                                            </Link>
                                            <button className="btn btn-primary hotel-book-btn">
                                                {t('hotels.bookNow', 'Book Now')}
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </motion.section>
            </div>
        </div>
    );
};

export default Hotels;
